import * as React from 'react';
import { isBrowser, throttle } from '@packages/common';

import { calculateResponsiveValues } from './utils';
import { Stage } from './Stage';

interface CarouselContextValue {
  slidingIndex: number;
  slidesCount: number;
  slidesToShow: number;
  wrapperWidth: number;
  slidesToMove: number;
  setSlidingIndex: React.Dispatch<React.SetStateAction<number>>;
  centered: boolean;
  slides: JSX.Element[];
}

export const CarouselContext = React.createContext({} as CarouselContextValue);

export interface CarouselProps {
  initialIndex?: number;
  slidesToShow: number | number[];
  breakpoints?: number[];
  slidesToMove?: number;
  slides: JSX.Element[];
  centered?: boolean | boolean[];
  afterSlide?: (slidingIndex: number, visibleIndexes: number[]) => void;
  dataset?: Record<string, string>;
}

const SLIDES_TO_SHOW = 3;
export const BREAKPOINTS = [480, 768, 992, 1250, 1550];

const slidesToShowForBreakpoint = (slidesToShow: number | number[], breakpoints: number[]) => {
  if (typeof slidesToShow === 'number') {
    return slidesToShow;
  }
  if (!isBrowser()) {
    return slidesToShow[slidesToShow.length - 1];
  }
  const currentBreakpoint = breakpoints.find((b) => window.innerWidth < b) || breakpoints[breakpoints.length - 1];
  return slidesToShow[breakpoints.indexOf(currentBreakpoint)] || slidesToShow[slidesToShow.length - 1];
};

export const Carousel: React.FC<CarouselProps> = ({
  children,
  slides = [],
  initialIndex = 0,
  slidesToShow = SLIDES_TO_SHOW,
  // slidesToMove = SLIDES_TO_SHOW,
  breakpoints = BREAKPOINTS,
  centered = false,
  dataset = {},
  afterSlide = () => {},
}) => {
  const wrapperRef = React.createRef<HTMLDivElement>();
  const isInitialized = React.useRef(false);
  const [slidingIndex, setSlidingIndex] = React.useState(initialIndex);
  const [wrapperWidth, setWrapperWidth] = React.useState<number>(0);
  const [responsiveValues, setResponsiveValues] = React.useState(
    calculateResponsiveValues({
      breakpoints,
      centered,
      slidesToShow,
    }),
  );
  const resizeHandler = React.useCallback(() => {
    if (wrapperRef?.current?.offsetWidth) {
      setWrapperWidth(wrapperRef.current.offsetWidth);
    }
  }, [wrapperRef]);

  React.useEffect(() => {
    setResponsiveValues(
      calculateResponsiveValues({
        breakpoints,
        centered,
        slidesToShow,
      }),
    );
  }, [breakpoints, centered, slidesToShow, wrapperWidth]);

  React.useEffect(() => {
    if (wrapperRef?.current && isInitialized?.current) {
      isInitialized.current = true;
    }
  }, [isInitialized, wrapperRef]);

  const resizeHandlerThrottled = throttle(resizeHandler, 200);

  React.useEffect(() => {
    resizeHandler();
  }, [resizeHandler]);

  React.useEffect(() => {
    window.addEventListener('resize', resizeHandlerThrottled);
    return () => {
      window.removeEventListener('resize', resizeHandlerThrottled);
    };
  }, [resizeHandlerThrottled]);

  React.useEffect(() => {
    const visibleIndexes = Array(slidesToShowForBreakpoint(slidesToShow, breakpoints))
      .fill(0)
      .map((_, index) => slidingIndex + index)
      // For infinite sliders
      .map((shownSlideIndex) =>
        shownSlideIndex > slides.length - 1 ? shownSlideIndex % (slides.length - 1) : shownSlideIndex,
      )
      // Deal with impreciese values
      .map((shownSlideIndex) => Math.round(shownSlideIndex));

    // adding a little bit of delay here, because the slide animation takes some time to finish
    setTimeout(() => afterSlide(slidingIndex, visibleIndexes), 400);
  }, [afterSlide, breakpoints, slides.length, slidesToShow, slidingIndex]);

  return (
    <CarouselContext.Provider
      value={{
        slides,
        slidingIndex,
        slidesCount: slides.length,
        slidesToShow: responsiveValues.slidesToShow,
        centered: responsiveValues.centered,
        wrapperWidth,
        setSlidingIndex,
        slidesToMove: responsiveValues.slidesToShow,
      }}
    >
      <div ref={wrapperRef} {...dataset}>
        {children}
      </div>
    </CarouselContext.Provider>
  );
};

const SliderItemWrapper: React.FC = React.memo(({ children }) => {
  return <>{children}</>;
});

SliderItemWrapper.displayName = 'SliderItemWrapper';

export const Slides: React.FC<{ lazy?: boolean }> = ({ lazy = true }) => {
  const { wrapperWidth, slidesToShow, slides, slidingIndex, centered } = React.useContext(CarouselContext);

  const alignedSlides = centered ? [slides[slides.length - 1], ...slides] : slides;
  const slidesToRender = lazy ? alignedSlides.slice(0, slidesToShow * 2 + slidingIndex) : alignedSlides;

  return wrapperWidth ? (
    <Stage>
      {slidesToRender.map((item, index) => (
        <div
          key={index}
          style={{
            width: `${wrapperWidth / slidesToShow}px`,
            boxSizing: 'border-box',
          }}
        >
          <SliderItemWrapper>{item}</SliderItemWrapper>
        </div>
      ))}
    </Stage>
  ) : (
    <></>
  );
};
